



// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import EmptyState from '@/shared/components/empty-state.component.vue';
import ItemThumb from '@/shared/components/item-thumb.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import Search from '@/shared/components/search.component.vue';
import Spinner from '@/shared/components/Spinner.component.vue';
import TopBar from '@/shared/components/TopBar.component.vue';
import Dropdown, { DropdownWidth } from '@/shared/components/Dropdown.component.vue';
import ButtonSwitch from '@/shared/components/button-switch.component.vue';

// Views
import ConvocationCard, { CardDecorationColor, CardNumber } from '@/shared/components/convocation-card/convocation-card.component.vue';

// Mixins
import SearchMixin from '@/shared/mixins/search.mixin.ts';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import SettingsMixin from '@/shared/mixins/settings.mixin.ts';

// Third party
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Constants
import Constants from '@/constants/constants';
import RouteNames from '@/constants/route-names';

// Services
import UtilsService from '@/core/services/utils.service';

// Filters
import formatDate from '@/shared/filters/formatDate.filter.ts';

interface MixinInterface extends WithPagination, SearchMixin, SettingsMixin {}

@Component({
    components: {
        Btn,
        ConvocationCard,
        ButtonSwitch,
        Dropdown,
        EmptyState,
        ItemThumb,
        Pagination,
        Search,
        Spinner,
        TopBar,
    },
    computed: {
        ...mapGetters ({
            convocationList: 'getConvocationsList',
        }),
    },
    filters: {
        formatDate,
    },
})
export default class PurchaseConvocationList extends Mixins<MixinInterface> (WithPagination, SearchMixin, SettingsMixin) {
    private constants = Constants;
    private utilsService = UtilsService;
    private convocationList!: any;
    private convocationListLoaded = false;
    private initialLoading = true;
    private cleanSearch = false;
    private timeSwitchSelected = {};
    private dateFrom: any = null;
    private dateTo: any = null;
    private ordering?: string;
    private productType?: string = this.constants.switchCourse.requestName;
    private productTypeSelected = this.constants.switchCourse;
    private dropdownWidth = DropdownWidth;
    private routeNames = RouteNames;
    private btnWidth = BtnWidth;
    private btnColor = BtnColor;


    @Watch('productTypeSelected')
    private watchProductTypeSelected(newVal: any, oldVal: any) {
        this.productType = newVal.requestName;
        this.pushQueryStrings();
    }

    private created() {
        this.initializePagination();
        this.initializeSettings();
        window.setTimeout(() => {
            this.initialLoading = false;
        }, 0);
    }


    get showPagination() {
        return this.convocationListLoaded && !this.spinner.show && this.convocationList.length > 0;
    }

    get showEmptyState() {
        return (this.convocationListLoaded && !this.spinner.show && this.convocationList.length === 0);
    }

    get numberOfConvocation() {
        const numConvocation = this.totalItems;
        let result = `${numConvocation} convocatorias`;
        if ( numConvocation === 1) {
            result = `${numConvocation} convocatoria`;
        }
        return result;
    }

    get timeFilter(): any[] {
        return [
            this.constants.switchPast,
            this.constants.switchFuture,
        ];
    }

    get productTypeOptions(): any[] {
        return [
            this.constants.switchConference,
            this.constants.switchCourse,
            this.constants.switchEvent,
        ];
    }

    public fetchList() {
        this.changeSpinnerVisibility( true );
        this.fetchConvocationList((res: any) => {
            this.onFetchSuccess(res);
            this.convocationListLoaded = true;
            this.changeSpinnerVisibility( false );
        });
    }

    public searchCallback() {
        this.pushQueryStrings();
    }

    public callbackRouteQueryChange() {
        this.getAllQueryParams();
        this.fetchFirstPageList();
    }

    public handleComeBack() {
        this.cleanSearch = !this.cleanSearch;
        this.searchString = '';
    }

    private convocationCardNumber(index: number) {
        let cardClass = CardNumber.Even;
        if ( index % 2 === 1 ) {
            cardClass = CardNumber.Odd;
        }

        return cardClass;
    }

    private initializePagination() {
        this.itemsPerPage = 25;
        this.limit = this.itemsPerPage;
    }

    private withYear( index: number ) {
        let withYear = false;
        if ( index === 0 ) {
            withYear = true;
        } else {
            const thisConvocation = this.convocationList[ index ];
            const previousConvocation = this.convocationList[ index - 1 ];
            if ( thisConvocation.timetables[0] !== undefined ) {
                const thisConvocationYear =
                    dayjs(thisConvocation.timetables[ 0 ].date, { locale: 'es' }).format('YYYY');
                const previousConvocationYear =
                    dayjs(previousConvocation.timetables[ 0 ].date, { locale: 'es' }).format('YYYY');
                if ( thisConvocationYear !== previousConvocationYear ) {
                    withYear = true;
                }
            }
        }
        return withYear;
    }

    private initializeSettings() {
        this.initialize(RouteNames.PurchaseConvocationList);
    }

    private getAllQueryParams() {
        this.getProductTypeParam();
        this.getTimeSwitchParam();
        this.getSearchParam();
    }

    private pushQueryStrings() {
        const productType = this.productType || '';
        const to = this.dateTo;
        const from = this.dateFrom;
        const search = this.searchString;
        this.$router.push({
            name: this.$route.name ,
            query: {
                product_type: productType,
                to,
                from,
                search,
            },
        });
    }

    private getProductTypeParam() {
        this.productType = this.constants.switchCourse.requestName;
        const routeValue = this.$route.query.product_type;
        if ( routeValue ) {
            this.productTypeSelected = this.productTypeOptions.find((option: any) => {
                return option.requestName === routeValue;
            });
            this.productType = this.productTypeSelected.requestName;
        }
    }

    private getTimeSwitchParam() {
        const from = this.$route.query.from;
        const to = this.$route.query.to;
        if ( from ) {
            this.dateFrom = from;
            this.timeSwitchSelected = this.constants.switchFuture;
        }
        if ( to ) {
            this.dateTo = to;
            this.timeSwitchSelected = this.constants.switchPast;
            this.ordering = '-initial_date';
        }
    }

    private getSearchParam() {
        const search = this.$route.query.search;
        this.searchString = search;
    }

    private timeFilterChanged(option: any) {
        this.dateFrom = null;
        this.dateTo = null;
        this.ordering = undefined;
        if (option.id === this.constants.switchPast.id) {
            this.dateTo = this.utilsService.yesterday();
            this.ordering = '-initial_date';
            this.timeSwitchSelected = this.constants.switchPast;
        } else if (option.id === this.constants.switchFuture.id) {
            this.dateFrom = this.utilsService.today();
            this.timeSwitchSelected = this.constants.switchFuture;
        }
        this.pushQueryStrings();
    }

    private getObjectFromStoredItem(requestName: string) {
        return this.productTypeOptions.find(
            (type: any) => {
                return type.requestName.toString() === requestName.toString();
            },
        );
    }

    private fetchConvocationList(callback: any = () => { const doNothing = 1; }) {
        const payload = {
            queryParams: {
                offset: this.offset,
                limit: this.limit,
                search: this.searchString,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                ordering: this.ordering,
                productType: this.productType,
            },
        };
        this.$store.dispatch('fetchConvocationList', payload)
            .then((res) => {
                callback(res);
            });
    }
}
